/**
 * Attaching a hub scan as the hub's 3D model — the last step of the scan → 3D home
 * path (card `t_92f7411f`, E3).
 *
 * A scan is hub-level (`/api/scans`, E1) and has no dashboard; the 3D view loads
 * the HUB's one model row (`/api/bim-models`, `t_hub_bim_model`). So "use this scan
 * as the model" is: read the geometry file out of the scan package through the
 * app's own transport, and hand it to the hub's model route — the same POST
 * `BimModelUploadModal` sends for a file picked from disk. Nothing here invents a
 * second way to attach a model, and nothing writes outside the two routes the hub
 * already owns.
 *
 * Two shape notes, both deliberate:
 *
 *   · **the file's content type is forced to `application/octet-stream`.** The
 *     hub's bim-model route accepts an octet-stream body or one of the three
 *     extensions it advertises; a scan's geometry may be a `.usdz` (the raw
 *     capture, before conversion), which the route's extension list does not
 *     name and which would otherwise travel as `model/vnd.usdz+zip` and be
 *     refused. The bytes are what the app's loader sniffs anyway — the route's
 *     own content type never decides how a model renders.
 *   · **the model keeps the scan's own name** (`scan.name`, then the file's name
 *     without its extension). That is the name the dashboard's editor shows as
 *     its current model, and the name a person recognises from the scan list.
 *
 * Failures are coded (`ScanAttachError`), never sentences: the surface that
 * shows them owns its language, exactly as `useScanUpload` does for a transfer.
 * Every call here is total: a scan with no geometry in it is refused before a
 * byte is fetched.
 */
import type { HubScan } from './scanApi';
import { BIM_MODELS_PATH } from '../utils/bimModelRoutes';
import { scanPrimaryModelName } from '../utils/scanPackageGeometry';
import { stripBimModelExtension } from '../utils/bimModelFormat';

/** Why a scan could not become a dashboard's model — the surface translates it. */
export type ScanAttachErrorCode = 'no-model' | 'fetch-failed' | 'attach-failed';

/** A failed attach, with the code a surface maps to its own sentence. */
export class ScanAttachError extends Error {
  readonly code: ScanAttachErrorCode;
  /** The cause's own words — the second half of the surface's sentence. */
  readonly detail: string;

  constructor(code: ScanAttachErrorCode, detail: string) {
    super(detail);
    this.name = 'ScanAttachError';
    this.code = code;
    this.detail = detail;
  }
}

function bodyOf(error: unknown): { error?: string; message?: string } {
  const data = (error as { data?: unknown }).data;
  return data && typeof data === 'object' ? (data as { error?: string; message?: string }) : {};
}

function detailOf(error: unknown): string {
  const body = bodyOf(error);
  return body.error || body.message || (error instanceof Error ? error.message : String(error));
}

/**
 * The one place a thrown error becomes a coded one (an already-coded one passes
 * through). Both halves below wrap their own failures, so the fallback here is
 * the umbrella sentence — "the scan could not become this dashboard's model".
 */
export function scanAttachError(error: unknown): ScanAttachError {
  if (error instanceof ScanAttachError) return error;
  return new ScanAttachError('attach-failed', detailOf(error));
}

/**
 * The file in a scan's package a dashboard's model is built from, or `null`
 * when the package carries no geometry — `scanPrimaryModelName`'s rule
 * (`model.glb` first, then the capture's own `*.usdz`), which is the same order
 * the settings page's download and the widget's picker read.
 */
export function scanModelFileName(scan: HubScan): string | null {
  return scanPrimaryModelName(scan);
}

/** Is there geometry in this package for the hub's 3D view to render? */
export function scanHasModel(scan: HubScan): boolean {
  return scanModelFileName(scan) !== null;
}

/** The transport seams a unit test replaces; the defaults are the app's own. */
export interface ScanModelAttachDeps {
  /** Fetch one file of a scan package. */
  fetchFile?: (scanId: string, fileName: string) => Promise<{ blob: Blob; contentType: string }>;
  /** POST the hub's model row (multipart, `bim_model` + `bim_model_name`). */
  post?: (endpoint: string, body: FormData) => Promise<unknown>;
}

/**
 * The app's own transport, imported LAZILY on purpose: `services/api.ts` reaches
 * the stores (`import.meta.env`, pinia), so a module-scope import would make this
 * file unimportable from a plain Node test — and this file's whole seam design is
 * that a test drives it with a stub. Same pattern as `useScanUpload.ts`'s
 * `@capacitor/filesystem`: the plugin/transport is loaded when it is used.
 */
async function fetchScanFileViaApp(scanId: string, fileName: string) {
  const { fetchScanFile } = await import('./scanApi');
  return fetchScanFile(scanId, fileName);
}

async function postModelRowViaApp(endpoint: string, body: FormData): Promise<unknown> {
  const { apiFetchRaw } = await import('./api');
  return apiFetchRaw(endpoint, { method: 'POST', body });
}

/** The scan package's geometry as a `File` the hub's model route accepts. */
export async function scanModelFile(scan: HubScan, deps: ScanModelAttachDeps = {}): Promise<File> {
  const fileName = scanModelFileName(scan);
  if (!fileName) {
    throw new ScanAttachError('no-model', `scan ${scan.id} carries no .glb/.gltf/.usdz file`);
  }
  const fetchFile = deps.fetchFile ?? fetchScanFileViaApp;
  let blob: Blob;
  try {
    ({ blob } = await fetchFile(scan.id, fileName));
  } catch (error) {
    throw error instanceof ScanAttachError ? error : new ScanAttachError('fetch-failed', detailOf(error));
  }
  return new File([blob], fileName, { type: 'application/octet-stream' });
}

/**
 * Attach a scan as the hub's model: the one model row becomes this scan's
 * geometry (the hub deletes the row it replaces and carries the rooms document
 * across — its own contract, none of this code's business).
 */
export async function attachScanModel(
  scan: HubScan,
  deps: ScanModelAttachDeps = {},
): Promise<void> {
  const file = await scanModelFile(scan, deps);
  const form = new FormData();
  form.append('bim_model', file);
  form.append('bim_model_name', scan.name?.trim() || stripBimModelExtension(file.name) || 'Scan');
  const post = deps.post ?? postModelRowViaApp;
  try {
    await post(BIM_MODELS_PATH, form);
  } catch (error) {
    throw error instanceof ScanAttachError ? error : new ScanAttachError('attach-failed', detailOf(error));
  }
}
